import React, { useMemo } from 'react';
import { StyledImage } from './styles';
import { DarkImages, LightImages } from '../../assets/images/images';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { isEmpty } from '../../helpers/common';
import { AVATAR_BORDER } from '../../global/constants';

export type ImageKey = keyof typeof LightImages | keyof typeof DarkImages;

/**
 * Props for the Image component.
 * @interface ImageProps
 * @property {number | string} [width] - Width of the image in pixels or CSS units.
 * @property {number | string} [height] - Height of the image in pixels or CSS units.
 * @property {string} [className] - Additional CSS class name for styling.
 * @property {'eager' | 'lazy'} [loading] - Loading behavior for the image.
 * @property {string} [src] - Direct image URL (for profile images, invoice logos, etc.).
 * @property {ImageKey} [imageKey] - Theme-based image key from LightImages/DarkImages for dark/light variants.
 * @property {string | ImageKey} [imageUrl] - Unified prop that accepts either direct URL or theme-based image key.
 * @property {string} [alt] - Alt text for accessibility and SEO.
 * @property {string} [borderRadius] - CSS border-radius for rounded corners.
 * @property {'contain' | 'cover' | 'fill' | 'none' | 'scale-down'} [objectFit] - CSS object-fit property for image scaling behavior.
 * @property {string} [padding] - CSS padding for spacing around the image.
 * @property {() => void} [onClick] - Click handler for the image.
 */
interface ImageProps {
  width?: number | string;
  height?: number | string;
  className?: string;
  loading?: 'eager' | 'lazy';
  src?: string; // Direct image URL
  imageKey?: ImageKey; // Theme-based image key
  imageUrl?: string | ImageKey; // Unified prop for both URL and theme key
  alt?: string;
  borderRadius?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  id?: string;
  padding?: string;
  onClick?: () => void;
  borderVariant?: (typeof AVATAR_BORDER)[keyof typeof AVATAR_BORDER];
}

const Image: React.FC<ImageProps> = ({
  width,
  height,
  className,
  loading,
  src,
  imageKey,
  imageUrl,
  alt,
  borderRadius,
  objectFit,
  padding,
  id,
  onClick,
  borderVariant
}) => {
  const { appStore } = rootStore;
  const currentTheme = appStore.themeMode?.theme;

  const getImageSource = useMemo(() => {
    // Priority 1: Direct src prop
    if (!isEmpty(src)) {
      return src;
    }

    // Priority 2: Unified image prop - check if it's a URL or theme key
    if (imageUrl) {
      // If it looks like a URL (contains http, data:, or starts with /), use as direct src
      if (
        typeof imageUrl === 'string' &&
        (imageUrl.includes('http') || imageUrl.startsWith('data:') || imageUrl.startsWith('/'))
      ) {
        return imageUrl;
      }
      // Otherwise treat as theme key
      if (typeof imageUrl === 'string' && Object.keys(LightImages).includes(imageUrl)) {
        return currentTheme === 'dark' ? DarkImages[imageUrl as ImageKey] : LightImages[imageUrl as ImageKey];
      }
    }

    // Priority 3: imageUrl prop (theme-based)
    if (imageKey) {
      return currentTheme === 'dark' ? DarkImages[imageKey] : LightImages[imageKey];
    }

    return '';
  }, [src, imageUrl, imageKey, currentTheme]);

  return (
    <StyledImage
      id={id}
      src={getImageSource}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      borderRadius={borderRadius}
      objectFit={objectFit}
      padding={padding}
      onClick={onClick}
      borderVariant={borderVariant}
    />
  );
};

export default observer(Image);
